import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { Member } from 'src/app/core/_models/member';
import { ProjectType } from 'src/app/core/_models/projectType';
import { Version } from 'src/app/core/_models/version';
import { MemberService } from 'src/app/core/_services/member.service';
import { ProjectTypeService } from 'src/app/core/_services/projectTypes.service';
import { VersionService } from 'src/app/core/_services/versions.service';
import { Energie } from '../../core/_models/energie';
import { Indicator } from '../../core/_models/indicator';
import { OperationType } from '../../core/_models/operationType';
import { EnergieService } from '../../core/_services/energie.service';
import { OperationService } from '../../core/_services/operation.service';
import { ProjectService } from '../../core/_services/project.service';
import { SolutionService } from '../../core/_services/solution.service';

export interface ProjectSharedData {
  energies: Energie[];
  vehicleEnergies: Energie[];
  operationTypes: OperationType[];
  biodiversityList: any[];
  waterList: any[];
  indicators: Indicator[];
  versions: Version[];
  moList: Member[];
  projectTypes: ProjectType[];
}

@Injectable({
  providedIn: 'root'
})
export class ProjectDataService {
  private dataSubject = new BehaviorSubject<ProjectSharedData | null>(null);
  private loadingSubject = new BehaviorSubject<boolean>(false);

  public readonly data$ = this.dataSubject.asObservable();
  public readonly loading$ = this.loadingSubject.asObservable();

  private dataCache: ProjectSharedData | null = null;
  private loadPromise: Promise<ProjectSharedData> | null = null;

  constructor(
    private energieService: EnergieService,
    private operationService: OperationService,
    private solutionService: SolutionService,
    private projectService: ProjectService,
    private versionService: VersionService,
    private memberService: MemberService,
    private projectTypeService: ProjectTypeService
  ) {}

  loadProjectData(): Observable<ProjectSharedData> {
    if (this.dataCache) {
      return of(this.dataCache);
    }

    if (this.loadPromise) {
      return new Observable((observer) => {
        this.loadPromise!.then((data) => {
          observer.next(data);
          observer.complete();
        }).catch((error) => {
          observer.error(error);
        });
      });
    }

    this.loadingSubject.next(true);

    this.loadPromise = forkJoin({
      energies: this.energieService.getAllForTransportation(),
      vehicleEnergies: this.energieService.getAllForVehicle(),
      operationTypes: this.operationService.getOperationTypes(),
      biodiversityList: this.solutionService.getBiodiversityList(),
      waterList: this.solutionService.getWaterList(),
      indicators: this.projectService.getIndicators(),
      versions: this.versionService.getAll(),
      moList: this.memberService.getAllProjectContractors(),
      projectTypes: this.projectTypeService.getAll()
    })
      .pipe(
        map((data) => ({
          ...data,
          indicators: data.indicators
            .filter((row: any) => ['EN', 'CO', 'AE', 'GN', 'DRC', 'MR', 'TK'].includes(row.code))
            .sort((a: Indicator, b: Indicator) => {
              if (a.code === 'CO') return -1;
              if (b.code === 'CO') return 1;
              return 0;
            })
        })),
        tap((data) => {
          this.dataCache = data;
          this.dataSubject.next(data);
          this.loadingSubject.next(false);
        }),
        shareReplay(1)
      )
      .toPromise() as Promise<ProjectSharedData>;

    return new Observable((observer) => {
      this.loadPromise!.then((data) => {
        observer.next(data);
        observer.complete();
      }).catch((error) => {
        observer.error(error);
        this.loadingSubject.next(false);
      });
    });
  }

  getEnergies(): Observable<Energie[]> {
    return this.data$.pipe(map((data) => data?.energies || []));
  }

  getOperationTypes(): Observable<OperationType[]> {
    return this.data$.pipe(map((data) => data?.operationTypes || []));
  }

  getIndicators(): Observable<Indicator[]> {
    return this.data$.pipe(map((data) => data?.indicators || []));
  }

  getBiodiversityList(): Observable<any[]> {
    return this.data$.pipe(map((data) => data?.biodiversityList || []));
  }

  getWaterList(): Observable<any[]> {
    return this.data$.pipe(map((data) => data?.waterList || []));
  }

  clearCache(): void {
    this.dataCache = null;
    this.loadPromise = null;
    this.dataSubject.next(null);
  }

  refreshData(): Observable<ProjectSharedData> {
    this.clearCache();
    return this.loadProjectData();
  }

  invalidateAndReload(): Observable<ProjectSharedData> {
    this.dataCache = null;
    this.loadPromise = null;
    this.dataSubject.next(null);
    return this.loadProjectData();
  }

  invalidateSpecificData(type: 'energies' | 'vehicleEnergies' | 'operationTypes' | 'indicators' | 'biodiversityList' | 'waterList'): void {
    if (this.dataCache) {
      this.dataCache[type] = [];
      this.dataSubject.next(this.dataCache);
    }
  }

  refreshSpecificData(type: 'energies' | 'vehicleEnergies' | 'operationTypes' | 'indicators' | 'biodiversityList' | 'waterList'): Observable<any[]> {
    this.invalidateSpecificData(type);

    let serviceCall: Observable<any[]>;
    switch (type) {
      case 'energies':
        serviceCall = this.energieService.getAllForTransportation();
        break;
      case 'vehicleEnergies':
        serviceCall = this.energieService.getAllForVehicle();
        break;
      case 'operationTypes':
        serviceCall = this.operationService.getOperationTypes();
        break;
      case 'indicators':
        serviceCall = this.projectService.getIndicators();
        break;
      case 'biodiversityList':
        serviceCall = this.solutionService.getBiodiversityList();
        break;
      case 'waterList':
        serviceCall = this.solutionService.getWaterList();
        break;
    }

    return serviceCall.pipe(
      tap((newData) => {
        if (this.dataCache) {
          this.dataCache[type] = newData;
          this.dataSubject.next(this.dataCache);
        }
      })
    );
  }

  getDataSync(): ProjectSharedData | null {
    return this.dataCache;
  }

  isDataLoaded(): boolean {
    return this.dataCache !== null;
  }

  ensureDataLoaded(): Observable<ProjectSharedData> {
    if (this.isDataLoaded()) {
      return of(this.dataCache!);
    }
    return this.loadProjectData();
  }
}
